/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {entityId} from '../services/ConnectUtils';
import {GenericObject, Link, Role, Roles, User} from '../types/entities';
import {idMap} from '../utils/ObjectUtil';

export type RoleMapping = {
  filteredRoles: Role[] | Link[],
  selectedRole: GenericObject;
};

/**
 * @function getUserRole
 *
 * Given a user and a collection of Roles, returns the users role from the given application roles.
 *
 * ```typescript
 *
 *     import {getUserRole} from '@ion/core/src/entities/Roles';
 *
 *     const role = getUserRole(user, roles);
 *
 * ```
 *
 * @param   {User} user The User instance for which their application role is to be returned.
 * @param   {Role[]} roles The collection of roles from which to search.
 * @param   {string} property A specific property from which to return. This argument is useful
 *          if only a certain value is needed, such as the Role's title for display, or id, etc..
 * @returns {Role | unknown} The matching Role instance for the given application.
 */
export const getUserRole = (user: User, roles: Role[], property?: string): Role | unknown => {
  const ids = idMap(roles);
  const role = user?.roles?.map(({href}: Link) => ids[entityId(href)]).filter((roleObj) => roleObj);
  return role?.length > 0 ? property ? role[0]?.[property] : role[0] : undefined;
};

/**
 * @function getUserRoleLink
 *
 * Given a user and a collection of Roles, searches the users assigned roles and returns the link
 * for the given application role.
 *
 * ```typescript
 *
 *     import {getUserRoleLink} from '@ion/core/src/entities';
 *
 *     const link = getUserRoleLink(user, roles);
 *
 * ```
 *
 * @param   {User} user The User instance for which their application role is to be returned.
 * @param   {Role[]} roles The collection of roles from which to search.
 * @returns {Link} The Role link from the users role assignments that is included in the roles list.
 */
export const getUserRoleLink = (user: User, roles: Role[]): Link => {
  const ids = idMap(roles);
  return user?.roles?.find(({href}: Link) => ids[entityId(href)]);
};

/**
 * Given a user and their current Role assignments, removes updates their assignments
 * to include the selected role and preserve all other existing roles not found in the
 * collection of Roles. This function is generally used to update a users list of role
 * assignments by removing their previously assigned application role, adding the newly
 * assigned application role, and preserving all other roles; those for other applications.
 *
 * ```typescript
 *
 *     import {updateUserRole} from '@ion/core/src/entities';
 *
 *     const roles = updateUserRoles(user, selected, roles);
 *
 * ```
 *
 * @param   {User} user The User instance for which their application role is to be returned.
 * @param   {Role[]} roles The collection of roles from which to search.
 * @returns {Link} The Role link from the users role assignments that is included in the roles list.
 * @param roles
 */
export const updateUserRoles = (user: User, role: Link, roles: Role[]): Link[] => {
  const ids = idMap(roles);
  const assigned = [...user.roles].filter(({href}: Link) => {
    return !ids[entityId(href)];
  });
  assigned.push(role);
  return assigned;
};

/**
 * The createRoleSearchTerms utility iterates the list of imported Users,
 * and returns an array of search terms for each group which
 * contains Role Name.
 *
 * ```typescript
 *
 * import {createRoleSearchTerms} from '@ion/core/src/entities';
 * const roleSearch = createRoleSearchTerms(resolved);
 * ```
 *
 * @param   resolved Array of resolved users which are to be imported.
 *          Users are considered to be resolved if the group to which they are
 *          to be added exists within the current global group hierarchy.
 * @returns An array of user searchTerm objects, whereby each object
 *          defines all Role Name values as the role name search term for each group.
 */
export const createRoleSearchTerms = (resolved) => {
  const groupRoles = {};
  const roles = [];
  resolved.forEach(({user}) => {
    const {account, roles} = user;
    if (roles?.length) {
      const groupId = entityId(account);
      groupRoles[groupId] = groupRoles[groupId] || [];
      groupRoles[groupId].push(entityId(roles[0].href));
    }
  });
  Object.keys(groupRoles).forEach(groupId => {
    roles.push({
      name : {
        value    : groupRoles[groupId].toString(),
        wildcard : false
      },
      account : {
        value : groupId
      }
    });
  });
  return roles;
};

/**
 * Replaces the role link for each user entity ...
 *
 * ```typescript
 *
 * import {replaceRoleLink} from '@ion/core/src/entities';
 * const user = replaceRoleLink(user, roles);
 * ```
 * @param user  the current user entity
 * @param roles the available roles for the current user entity
 */
const replaceRoleLink = (user, roles) => {
  if (user?.role) {
    const roleName = user.role?.toLowerCase();
    Object.keys(roles)?.forEach(groupId => {
      const items = roles[groupId];
      if (items?.length) {
        items.forEach(role => {
          if (roleName === role?.name?.toLowerCase()) {
            user.roles = [{
              href : role?._self?.href,
              rel  : 'role'
            }];
          }
        });
      }
    });
  }
};

/**
 * Provides a convenience function which validates the Role Name for the
 * list of imported entities against the Roles retrieved, returning an object
 * which contains the valid and invalid entities.
 *
 * ```typescript
 *
 * import {validateRoleGroups} from '@ion/core/src/entities';
 * const {valid, invalid} = validateRoleGroups(resolved, roles);
 * ```
 *
 * @param   resolved entities containing role which are to be validated.
 * @param   roles A mapping of group ids which contains the role
 *          search results for each entity which defined a role name.
 * @returns An object providing a valid property which contains the
 *          list of valid entities with roles, and an invalid property which
 *          contains the list of invalid entities with roles (i.e. either the
 *          role could not be found, or the role is not defined within
 *          the current group hierarchy).
 */
export const validateRoleGroups = (resolved, roles) => {
  const valid     = [];
  const invalid   = [];
  const validated = {};
  Object.keys(roles)?.forEach(groupId => {
    const results = roles[groupId];
    results?.forEach(({name, _self}) => {
      validated[name?.toLowerCase()] = _self.href;
    });
  });
  resolved?.forEach(({user}) => {
    const {roles: userRole} = user;
    if (userRole?.length && !validated[entityId(userRole[0])?.toLowerCase()]) {
      invalid.push({user});
    } else {
      valid.push({user});
    }
    replaceRoleLink(user, roles);
  });
  return {valid, invalid};
};

/**
 * Provides a utility which will map the filter the Roles based on the
 * Application Key.
 */
export const getApplicationRoles = (appKey: String, roles: Roles): Roles => {
  return roles.filter(({application}) => entityId(application?.href, false) === appKey);
};

/**
 * Provides a utility which will map the filter the Roles based on the
 * Application Key and selected Roles.
 */
export const applicationRoleMapping = (
  appkey: string,
  selectedRoles: GenericObject,
  roles: Roles
): RoleMapping => {
  const filteredRoles = roles?.filter(({application}) => application.title === appkey);
  const role = filteredRoles
    ? filteredRoles.find((data) => selectedRoles?.find(({title}) => data.name === title)) : null;
  return {
    filteredRoles,
    selectedRole : role
  };
};

/**
 * Provides a utility sorting the array of object
 */
export const sortFunction = (data, prop='name') => {
  return data?.sort((a, b) => a?.[prop].toUpperCase() > b?.[prop].toUpperCase() ? 1 : -1);
};

/**
 * Provides a utility which will get the role id from the href.
 *
 */
export const getRoleIds = (roles: Link[]): number[] => {
  return roles.map(({href}) => entityId(href));
};
